import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { LogTripModal } from '../components/LogTripModal.js';
import { OfflineQueueNotice } from '../components/OfflineQueueNotice.js';
import { useT } from '../i18n/useT.js';
import { formatDate, formatMoney } from '../format.js';
import {
  useDeleteTrip,
  useExportTrips,
  usePostTrip,
  useTripYearSummary,
  useTrips,
} from '../api/hooks.js';
import type { Trip } from '../api/hooks.js';

/**
 * Kørebog — the logbook, and the page it is kept from.
 *
 * ## Mobile first, and meant literally
 *
 * Every other page here was designed at a desk and made to survive a narrow
 * screen. This one is the other way round: a trip is logged standing beside a
 * car, one-handed, with the engine still running. So "Log tur" is a full-width
 * button at the top rather than an action in a table header, the form asks five
 * questions and no more, and the date defaults to today because it almost
 * always is.
 *
 * ## Why the rate is shown and never offered
 *
 * The satser are Skatterådets and are looked up from the trip's date. Putting
 * them in the form as a field — even a pre-filled one — would invite somebody
 * to change one, and a kørebog priced at a rate the state did not set is not a
 * kørebog. They are displayed, with the bekendtgørelse each came from, so the
 * number can be checked and not edited.
 *
 * ## Why an unposted trip can be deleted and a posted one cannot
 *
 * A trip logged by mistake never became an accounting record, and a logbook
 * padded with retracted rows is worse evidence than one without them. Once it
 * has produced a journal entry, the entry is immutable and so is the trip.
 *
 * The form itself is `LogTripModal`, shared with the installed mobile app. The
 * home-screen shortcut that used to open it here (`?ny=1`) now opens it in the
 * app instead — see CaptureActions.
 */

// -----------------------------------------------------------------------------
// One row
// -----------------------------------------------------------------------------

function TripRow({ trip }: { readonly trip: Trip }): ReactNode {
  const t = useT();
  const post = usePostTrip();
  const remove = useDeleteTrip();
  const [confirming, setConfirming] = useState<'post' | 'delete' | null>(null);

  const posted = trip.journalEntryId !== null;
  const straddles = !new Decimal(trip.tier2Km).isZero();

  return (
    <>
      <Table.Tr>
        <Table.Td>{formatDate(trip.date)}</Table.Td>
        <Table.Td>
          <Text size="sm">
            {trip.startAddress} → {trip.endAddress}
          </Text>
          <Text size="xs" c="dimmed">
            {trip.purpose}
          </Text>
        </Table.Td>
        <Table.Td ta="right">{formatMoney(trip.distanceKm)}</Table.Td>
        <Table.Td ta="right">
          {trip.isBusiness ? formatMoney(trip.kmRate) : '—'}
          {straddles && (
            <Text size="xs" c="dimmed">
              {t('trip.splitAcrossTiers', {
                tier1: formatMoney(trip.tier1Km),
                tier2: formatMoney(trip.tier2Km),
              })}
            </Text>
          )}
        </Table.Td>
        <Table.Td ta="right">{formatMoney(trip.deductionAmount)}</Table.Td>
        <Table.Td>
          {!trip.isBusiness ? (
            <Badge color="gray" variant="light">
              {t('trip.private')}
            </Badge>
          ) : posted ? (
            <Badge color="green" variant="light">
              {t('trip.statusPosted')}
            </Badge>
          ) : (
            <Badge color="yellow" variant="light">
              {t('trip.statusDraft')}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>
          <Group gap="xs" justify="flex-end" wrap="nowrap">
            {!posted && trip.isBusiness && (
              <Button
                size="compact-sm"
                onClick={() => {
                  setConfirming('post');
                }}
              >
                {t('trip.post')}
              </Button>
            )}
            {!posted && (
              <Button
                size="compact-sm"
                variant="subtle"
                color="red"
                onClick={() => {
                  setConfirming('delete');
                }}
              >
                {t('trip.delete')}
              </Button>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>

      <Modal
        opened={confirming !== null}
        onClose={() => {
          setConfirming(null);
        }}
        title={
          confirming === 'delete' ? t('trip.deleteConfirmTitle') : t('trip.postConfirmTitle')
        }
      >
        <Stack gap="md">
          <Text size="sm">
            {confirming === 'delete'
              ? t('trip.deleteConfirm')
              : t('trip.postConfirm', {
                  amount: `${formatMoney(trip.deductionAmount)} kr`,
                })}
          </Text>
          {post.isError && <ErrorMessage error={post.error} title={t('trip.postFailed')} />}
          {remove.isError && (
            <ErrorMessage error={remove.error} title={t('trip.deleteFailed')} />
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setConfirming(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              {...(confirming === 'delete' ? { color: 'red' } : {})}
              loading={post.isPending || remove.isPending}
              onClick={() => {
                const action = confirming === 'delete' ? remove : post;
                void action.mutateAsync(trip.id).then(
                  () => {
                    notifications.show({
                      color: 'green',
                      title: confirming === 'delete' ? t('trip.deleted') : t('trip.posted'),
                      message: '',
                    });
                    setConfirming(null);
                  },
                  () => {
                    // The error is rendered above; the dialog stays open so the
                    // reason is read rather than dismissed.
                  },
                );
              }}
            >
              {confirming === 'delete' ? t('trip.delete') : t('trip.post')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// -----------------------------------------------------------------------------
// The page
// -----------------------------------------------------------------------------

function yearOptions(): string[] {
  const current = new Date().getFullYear();
  return [current, current - 1, current - 2].map(String);
}

export function TripsPage(): ReactNode {
  const t = useT();
  const [opened, { open, close }] = useDisclosure(false);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const trips = useTrips({ from: `${year}-01-01`, to: `${year}-12-31` });
  const summary = useTripYearSummary(Number(year));
  const exportTrips = useExportTrips();

  function download(): void {
    exportTrips.mutate(Number(year), {
      onSuccess: ({ url, filename }) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        // Revoked on the next tick: revoking synchronously can cancel the
        // download before the browser has read the blob.
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 0);
      },
    });
  }

  const tripItems = trips.data?.items ?? [];
  const rates = summary.data?.rates ?? [];
  /*
    The rate in force for the year being looked at, not the newest one.

    `rates` is newest first, so this is the newest row that had taken effect by
    the end of the selected year. Taking `rates[0]` showed 2026's 3,94 kr/km
    beside 2025's totals, which were priced at 3,81 -- on the one page whose job
    is to let those figures be checked.
  */
  const yearEnd = `${year}-12-31`;
  const currentRate = rates.find((rate) => rate.validFrom <= yearEnd);
  const businessKm = summary.data?.totalBusinessKm ?? '0';
  const threshold = currentRate?.thresholdKm ?? 20_000;
  const usedFraction = Math.min(
    100,
    (Number(businessKm) / Math.max(threshold, 1)) * 100,
  );

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>{t('trip.title')}</Title>
        <Text c="dimmed" size="sm">
          {t('trip.intro')}
        </Text>
      </Stack>

      <OfflineQueueNotice />

      {/*
        Full width and first. This is the one action the page exists for, and on
        a phone it should be reachable with a thumb without scrolling.
      */}
      <Button size="xl" onClick={open}>
        {t('trip.log')}
      </Button>

      <Group justify="space-between" wrap="wrap">
        <Select
          label={t('trip.year')}
          data={yearOptions()}
          value={year}
          onChange={(value) => {
            setYear(value ?? String(new Date().getFullYear()));
          }}
          allowDeselect={false}
          w={140}
        />
        <Button
          variant="light"
          onClick={download}
          loading={exportTrips.isPending}
          mt={{ base: 0, sm: 25 }}
        >
          {t('trip.export')}
        </Button>
      </Group>

      {exportTrips.isError && (
        <ErrorMessage error={exportTrips.error} title={t('trip.exportFailed')} />
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Card withBorder padding="md">
          <Text size="sm" c="dimmed">
            {t('trip.ytd')}
          </Text>
          <Text fw={700} size="xl">
            {formatMoney(businessKm)} km
          </Text>
          {/*
            The threshold is the one number in this domain a person cannot work
            out in their head, and crossing it halves what the driving is worth.
            A bar rather than a sentence, because it is a running total.
          */}
          <Progress value={usedFraction} mt="xs" color={usedFraction >= 100 ? 'orange' : 'blue'} />
          <Text size="xs" c="dimmed" mt={4}>
            {t('trip.ytdOfThreshold', {
              km: formatMoney(businessKm, 0),
              threshold: String(threshold),
            })}
          </Text>
        </Card>

        <Card withBorder padding="md">
          <Text size="sm" c="dimmed">
            {t('trip.totalDeduction')}
          </Text>
          <Text fw={700} size="xl">
            {formatMoney(summary.data?.totalDeduction ?? '0')} kr
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {t('trip.unposted')}: {formatMoney(summary.data?.unpostedDeduction ?? '0')} kr
          </Text>
        </Card>

        <Card withBorder padding="md">
          <Text size="sm" c="dimmed">
            {t('trip.rates')}
          </Text>
          {currentRate === undefined ? (
            <Text size="sm">—</Text>
          ) : (
            <Stack gap={2}>
              <Text size="sm">
                {t('trip.rateTier1', { threshold: String(currentRate.thresholdKm) })}:{' '}
                <strong>{formatMoney(currentRate.rateTier1Dkk)} kr/km</strong>
              </Text>
              <Text size="sm">
                {t('trip.rateTier2', { threshold: String(currentRate.thresholdKm) })}:{' '}
                <strong>{formatMoney(currentRate.rateTier2Dkk)} kr/km</strong>
              </Text>
              <Text size="xs" c="dimmed">
                {t('trip.rateSource')}: {currentRate.source}
              </Text>
            </Stack>
          )}
        </Card>
      </SimpleGrid>

      {usedFraction >= 100 && (
        <Alert color="orange" variant="light">
          {t('trip.thresholdPassed', { threshold: String(threshold) })}
        </Alert>
      )}

      {(summary.data?.unpostedTripCount ?? 0) > 0 && (
        <Alert color="yellow" variant="light">
          {t('trip.unpostedCount', { count: summary.data?.unpostedTripCount ?? 0 })}
        </Alert>
      )}

      {trips.isError && <ErrorMessage error={trips.error} title={t('trip.loadFailed')} />}

      {trips.isPending ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : tripItems.length === 0 ? (
        <Text c="dimmed">{t('trip.empty')}</Text>
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('trip.col.date')}</Table.Th>
                <Table.Th>{t('trip.col.route')}</Table.Th>
                <Table.Th ta="right">{t('trip.col.km')}</Table.Th>
                <Table.Th ta="right">{t('trip.col.rate')}</Table.Th>
                <Table.Th ta="right">{t('trip.col.amount')}</Table.Th>
                <Table.Th>{t('trip.col.status')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tripItems.map((trip) => (
                <TripRow key={trip.id} trip={trip} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Alert color="gray" variant="light">
        {t('trip.legalNote')}
      </Alert>

      <LogTripModal opened={opened} onClose={close} />
    </Stack>
  );
}
